import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import FeatureHeader from "../components/FeatureHeader";
import { FileText, FlaskConical, Stethoscope, ClipboardX, ArrowLeft } from "lucide-react";

export default function SharedRecordsViewer() {
  const { recordIds } = useParams();
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get("patientId");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchSharedRecords = async () => {
      if (!recordIds || !patientId) {
        setError("Missing record information");
        setLoading(false);
        return;
      }

      const ids = recordIds.split(",").filter(Boolean);
      if (ids.length === 0) {
        setError("No records specified");
        setLoading(false);
        return;
      }

      try {
        const [testsRes, docsRes, prescriptionsRes, consultationsRes] = await Promise.all([
          api.get(`/api/labtests`).catch(() => ({ data: { tests: [] } })),
          api.get(`/api/upload-health-records/${patientId}`).catch(() => ({ data: { documents: [] } })),
          api.get(`/api/prescription/patient/${patientId}`).catch(() => ({ data: { prescriptions: [] } })),
          api.get(`/api/consultations/patient/${patientId}`).catch(() => ({ data: { consultations: [] } })),
        ]);

        const allRecords = [
          ...(testsRes.data?.tests || []).map((test) => ({
            id: test._id,
            title: test.tests?.[0]?.testId?.name || test.name || "Lab Test",
            type: "Lab Test",
            date: test.createdAt || test.appointmentTime,
            details: test,
            icon: <FlaskConical size={18} color="#22c55e" />,
            color: "#22c55e",
            bg: "#ecfdf5",
          })),
          ...(docsRes.data?.documents || []).map((doc) => ({
            id: doc._id,
            title: doc.title || "Medical Record",
            type: "Document",
            date: doc.createdAt,
            details: doc,
            icon: <FileText size={18} color="#7c3aed" />,
            color: "#7c3aed",
            bg: "#f5f3ff",
          })),
          ...(prescriptionsRes.data?.prescriptions || []).map((rx) => ({
            id: rx._id,
            title: rx.medicineName || "Prescription",
            type: "Prescription",
            date: rx.issuedDate || rx.createdAt,
            details: rx,
            icon: <ClipboardX size={18} color="#f59e0b" />,
            color: "#f59e0b",
            bg: "#fff7ed",
          })),
          ...(consultationsRes.data?.consultations || []).map((appt) => ({
            id: appt._id,
            title: appt.doctor_?.name || appt.doctor?.name || "Doctor Consultation",
            type: "Consultation",
            date: appt.appointmentTime,
            details: appt,
            icon: <Stethoscope size={18} color="#0ea5e9" />,
            color: "#0ea5e9",
            bg: "#f0f9ff",
          })),
        ];

        const filtered = allRecords.filter((r) => ids.includes(r.id));
        setRecords(filtered);
      } catch (err) {
        console.error("Error fetching shared records:", err);
        setError("Failed to load records");
      } finally {
        setLoading(false);
      }
    };

    fetchSharedRecords();
  }, [recordIds, patientId]);

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
      <FeatureHeader
        title="Shared Health Records"
        subtitle="Records shared by patient"
        onBack={() => window.history.back()}
      />

      <div style={{ padding: "16px 18px" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 40, color: "#6b7280" }}>
            Loading records...
          </div>
        ) : error ? (
          <div style={{ textAlign: "center", padding: 40, color: "#ef4444" }}>
            {error}
          </div>
        ) : records.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: "#6b7280" }}>
            No records found or access denied.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {records.map((record) => (
              <div
                key={record.id}
                style={{
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 16,
                  padding: 16,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: record.bg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {record.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>
                    {record.title}
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                    {record.type} • {record.date ? new Date(record.date).toLocaleDateString() : "Recent"}
                  </div>
                  {record.details && (
                    <div style={{ marginTop: 8, fontSize: 12, color: "#374151", background: "#f9fafb", padding: 10, borderRadius: 8 }}>
                      <pre style={{ margin: 0, whiteSpace: "pre-wrap", fontFamily: "inherit" }}>
                        {JSON.stringify(record.details, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
